import * as React from 'react';
import { useState, useEffect } from 'react';
import { useRef } from 'react';
import Paper from '@mui/material/Paper';
import { Box, Typography, Chip } from '@mui/material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TT_BG_COLOR } from '../../../common/AppConstants';
import { MOCK_TT_TEMPLATE, MOCK_TT_DATA, MOCK_TT_RELATED_DATA } from './MockTimeTableData';

const MIN_CELL_WIDTH = 100;
const CELL_HEIGHT = 50;
const CELL_PADDING_RIGHT = 12;
const CELL_PADDING_BOTTOM = 4;
const GRID_BORDER = '1px solid #ddd';
const TILE_BORDER_RADIUS = '6px';
const TIME_SLOT_COLOR = '#f0f0f0';
const COLOURED_BAR_WIDTH = TILE_BORDER_RADIUS;

const MockTimeTable = () => {
  console.log("### RENDERING MockTimeTable ###");
  const viewType = 'student'; // student, teacher, classroom
  const viewElement = '3'; // particular element of viewType

  const template = MOCK_TT_TEMPLATE;
  const scheduleData = MOCK_TT_DATA;
  const scheduleRelatedData = MOCK_TT_RELATED_DATA;

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1000);

  useEffect(() => {
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.offsetWidth);
        }
      });
      resizeObserver.observe(containerRef.current);

      return () => resizeObserver.disconnect();
    }
  }, []);

  // Determine how many days ( columns - 1 ) to show based on screen size
  const getVisibleDaysCount = () => {
    // ( contianerWidth - cellWidth ) / cellWidth
    const maxDays = Math.floor((containerWidth - MIN_CELL_WIDTH) / MIN_CELL_WIDTH );
    console.log(`Possible maxColumns = ${maxDays + 1} for ${containerWidth}`);
    return Math.min(template.day_list.length, maxDays);
  };

  const visibleDayCount = getVisibleDaysCount();

  const dynamicCellWidth = Math.max(
    MIN_CELL_WIDTH,
    Math.floor(containerWidth / (visibleDayCount + 1)),
  );

  function TableCell({ rowIndex, colIndex, tenancy, children }) {

    // tenancy > 1 && ( viewType !== 'section' || !sameElectiveGroup(tenancy) ) ? TT_BG_COLOR.CONFLICT :
    const bgColor = colIndex < 0 ? TT_BG_COLOR.HEADER : (
      tenancy > 1 ? TT_BG_COLOR.CONFLICT :
      template.day_list[colIndex].time_list[rowIndex].reserved ? TT_BG_COLOR.RESERVED :
      template.day_list[colIndex].time_list[rowIndex].break ? TT_BG_COLOR.BREAK :
      TT_BG_COLOR.CELL
    );

    return (
      <Box
        sx={{
          height: `${CELL_HEIGHT}px`,
          width: '100%',
          // width: `${dynamicCellWidth}px`,
          borderRight: GRID_BORDER,
          borderBottom: GRID_BORDER,
          borderLeft: colIndex === -1 ? GRID_BORDER : 'none',
          // borderTop: colIdx === -1 ? TIME_SLOT_COLOR : "none",
          borderBottomLeftRadius:
            rowIndex === template.time_label_list.length - 1 && colIndex === -1
              ? TILE_BORDER_RADIUS
              : 0,
          borderBottomRightRadius:
            rowIndex === template.time_label_list.length - 1 &&
            colIndex === template.day_list.length - 1
              ? TILE_BORDER_RADIUS
              : 0,
          backgroundColor:
            bgColor,
        }}
      >
        {children}
      </Box>
    );
  }

  function TimeLabel({ top, left, width, rowIndex, cell }) {
    const cellData = cell;

    return (
      <Paper
        sx={{
          position: 'absolute',
          top: top,
          left: left + 1,
          height: CELL_HEIGHT - 1,
          width: width - 2,
          backgroundColor: TIME_SLOT_COLOR,
          borderRadius: 0,
          boxShadow: 0,
        }}
      >
        {/* Content */}
        <Box sx={{ px: 1, width: '100%', alignContent: 'center' }}>
          <Typography
            variant="body1"
            fontWeight="400"
            textAlign="center"
            // sx={{ fontSize: '0.8rem' }}
          >
            {cellData.label}
          </Typography>
        </Box>
      </Paper>
    );
  }

  function TableTile({ top, left, width, rowIndex, colIndex, index, cell, onClick }) {
    const cellData = scheduleData['raw'][cell.id];
    const height = (scheduleRelatedData.subject_component[cellData.subject_component.id].slots_per_session) * CELL_HEIGHT;
    const finalHeight = height - CELL_PADDING_BOTTOM;

    return (
      <Paper
        sx={{
          position: 'absolute',
          top: top,
          left: left,
          height: finalHeight,
          boxShadow: 0,
          width: width - CELL_PADDING_RIGHT,
          borderRadius: TILE_BORDER_RADIUS,
          backgroundColor: scheduleRelatedData.subject[scheduleRelatedData.subject_component[cellData.subject_component.id].subject].color || '#fff',
          cursor: 'grab',
          opacity: 1,
          flexDirection: 'row',
          zIndex: 10 + index,
          display: 'flex', // flex layout for the bar and content
        }}
        onClick={onClick}
      >
        {/* Colored bar */}
        <Box
          sx={{
            width: COLOURED_BAR_WIDTH,
            height: '100%',
            backgroundColor: scheduleRelatedData.subject_component_type[scheduleRelatedData.subject_component[cellData.subject_component.id].subject_component_type].color,
            flexShrink: 0,
            borderTopLeftRadius: TILE_BORDER_RADIUS,
            borderBottomLeftRadius: TILE_BORDER_RADIUS,
          }}
        />
        {/* Tile Text Content */}
        <Box
          sx={{ flexGrow: 1, py: 0.25, px: 0.5, width: '100%', alignContent: 'top', overflow: 'hidden' }}
        >
          <Typography
            variant="body1"
            fontWeight="400"
            textAlign="left"
            sx={{ fontSize: '0.8rem', mb: 0.25 }}
          >
            {scheduleRelatedData.subject[scheduleRelatedData.subject_component[cellData.subject_component.id].subject].code || cellData.label}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'left' }}>
            {['student', 'teacher', 'classroom']
              .filter((key) => key !== viewType)
              .map((key) => (
                <Chip
                  key={key}
                  label={scheduleRelatedData[key][cellData[key].id]?.code}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 16,
                    minWidth: 32,
                    fontSize: '0.6rem',
                    borderColor: '#cccccc',
                    borderRadius: 1, // This makes it rectangular with rounded corners
                    '& .MuiChip-label': { px: 0.5 }
                  }}
                />
              ))}
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Table */}
      <DndProvider backend={HTML5Backend}>
        <Box
          display="flex"
          flexDirection="column"
          sx={{ overflowX: 'auto' }}
        >
          {/* Table */}
          <Box flexGrow={1}>
            {/* Column Headers */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${visibleDayCount + 1}, ${dynamicCellWidth}px)`,
                // mb: 1,
              }}
            >
              {/* Top-left label */}
              <Box
                key="table-head-label"
                sx={{
                  width: `${dynamicCellWidth}px`,
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: GRID_BORDER,
                  borderBottom: GRID_BORDER,
                  borderLeft: GRID_BORDER,
                  borderTopLeftRadius: TILE_BORDER_RADIUS,
                  borderTopRightRadius: 0,
                  borderTop: GRID_BORDER,
                  backgroundColor: '#f0f0f0',
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="400"
                  textAlign="center"
                  // sx={{ fontSize: '0.8rem' }}
                >
                  {template.label}
                </Typography>
              </Box>

              {/* Day Headers */}
              {template.day_list.slice(0, visibleDayCount).map((day_dict, idx) => (
                <Box
                  key={`day-header-${idx}`}
                  sx={{
                    width: `${dynamicCellWidth}px`,
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRight: GRID_BORDER,
                    borderBottom: GRID_BORDER,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderTopRightRadius:
                      idx === visibleDayCount - 1
                        ? TILE_BORDER_RADIUS
                        : 0,
                    borderTop: GRID_BORDER,
                    backgroundColor: '#f0f0f0',
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight="400"
                    textAlign="center"
                    // sx={{ fontSize: '0.8rem' }}
                  >
                    {day_dict.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Grid Scaffold */}
            <Box sx={{ position: 'relative', width: '100%' }}>
              {/* Grid background */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${visibleDayCount + 1}, ${dynamicCellWidth}px)`,
                  gridTemplateRows: `repeat(${template.time_label_list.length}, ${CELL_HEIGHT}px)`,
                }}
              >
                {template.time_label_list.map((time_label, rowIdx) => (
                  <React.Fragment key={`row-${rowIdx}`}>
                    <TableCell
                      key={`time-label-${rowIdx}`}
                      rowIndex={rowIdx}
                      colIndex={-1}
                      tenancy={0}
                    >
                      <TimeLabel
                        key={`time-${rowIdx}`}
                        top={rowIdx * CELL_HEIGHT}
                        left={0}
                        width={dynamicCellWidth}
                        rowIndex={rowIdx}
                        cell={time_label}
                      />
                    </TableCell>
                    {/* Schedule data cells for each day */}
                    {scheduleData[viewType][viewElement].slice(0, visibleDayCount).map(
                      (day, colIdx) => {
                        const cellItems = day[rowIdx].tenant_list;
                        return (
                          <TableCell
                            key={`tabel-cell-${rowIdx}-${colIdx}`}
                            rowIndex={rowIdx}
                            colIndex={colIdx}
                            tenancy={day[rowIdx].tenancy}
                          >
                            {cellItems.map((cc_class, classIndex) => (
                              <TableTile
                                key={`tabel-tile-${rowIdx}-${colIdx}-${classIndex}`}
                                top={rowIdx * CELL_HEIGHT}
                                left={(colIdx + 1) * dynamicCellWidth}
                                width={dynamicCellWidth}
                                rowIndex={rowIdx}
                                colIndex={colIdx}
                                index={classIndex}
                                cell={cc_class}
                                // onClick={() => handleTileClick(cc_class)}
                              />
                            ))}
                          </TableCell>
                        );
                      },
                    )}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </DndProvider>
    </Box>
  );
};

export default MockTimeTable;
